// ============================================================================
// Client HTTP pour appeler l'API Officina
// ============================================================================
// Wrapper autour de fetch qui :
// - Ajoute automatiquement le token JWT stocké en localStorage
// - Gère les erreurs 401 en redirigeant vers /login
// - Sérialise/désérialise JSON
// - Fournit les types de retour
// ============================================================================

import { hasClerkSession, getFreshClerkToken, clerkEmail } from '../lib/clerkSso';
import { counter } from '../lib/counterMode';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const TOKEN_KEY = 'officina_token';
/**
 * Officine active (multi-site). Elle est stockée À PART du jeton, et envoyée en
 * en-tête `X-Site-Id` à chaque appel.
 *
 * Pourquoi pas simplement le jeton de bascule ? Parce que sur
 * `rh.pharmagestion.fr` chaque requête part avec un jeton Clerk FRAIS (voir plus
 * bas), qui écrasait celui délivré par `/sites/:id/switch` : la bascule ne
 * tenait pas une requête et le titulaire retombait sur sa pharmacie d'origine.
 * L'en-tête, lui, ne dépend pas du mode d'authentification.
 */
const SITE_KEY = 'officina_site';

/**
 * Jeton de renouvellement. Le backend en délivre un à chaque `/login`,
 * `/team-login` et `/register` — il était jusqu'ici **jeté**, et le JWT d'accès
 * expirant au bout de `JWT_EXPIRES_IN` (12 h), le premier appel d'après
 * éjectait le titulaire vers `/login` en plein travail. Il vit à côté du jeton
 * d'accès, et sert à en obtenir un neuf sans redemander le mot de passe.
 */
const REFRESH_KEY = 'officina_refresh';

export const auth = {
  getToken(): string | null {
    // Poste comptoir : la machine porte son propre jeton, et la session de
    // gestion ouverte par le PIN le remplace tant qu'elle dure. Le compte
    // personnel de qui a installé le poste n'entre jamais dans cette branche.
    if (counter.isPost()) return counter.unlockedToken() ?? counter.token();
    return localStorage.getItem(TOKEN_KEY);
  },
  setToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
  },
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  setRefreshToken(token: string) {
    localStorage.setItem(REFRESH_KEY, token);
  },
  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    // Un jeton s'en va, l'officine active aussi : le compte suivant ne doit pas
    // hériter du site du précédent.
    localStorage.removeItem(SITE_KEY);
  },
  /** Officine active, ou `null` = celle que porte le jeton. */
  getSite(): string | null {
    return localStorage.getItem(SITE_KEY);
  },
  setSite(siteId: string) {
    localStorage.setItem(SITE_KEY, siteId);
  },
  clearSite() {
    localStorage.removeItem(SITE_KEY);
  },
  isAuthenticated(): boolean {
    return !!this.getToken();
  },
};

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * Renouvellement en cours, s'il y en a un.
 *
 * Une page d'Officina lance volontiers dix requêtes d'un coup (planning,
 * salariés, compteurs…). À l'expiration, elles prennent toutes un 401 en même
 * temps. Sans ce garde, chacune consommerait le refresh token — or le backend
 * les fait TOURNER : la première rotation révoque celui que les neuf autres
 * s'apprêtent à présenter, et neuf échecs déconnecteraient le titulaire alors
 * que le renouvellement vient de réussir. On ne renouvelle donc qu'une fois, et
 * tout le monde attend la même promesse.
 */
let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = auth.getRefreshToken();
  if (!refreshToken) return null;

  const response = await fetch(`${API_URL}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) return null;

  const data = (await response.json()) as { token?: string; refreshToken?: string };
  if (!data.token || !data.refreshToken) return null;

  auth.setToken(data.token);
  // Rotation : l'ancien vient d'être révoqué côté serveur, le garder nous
  // laisserait un jeton mort en poche.
  auth.setRefreshToken(data.refreshToken);
  return data.token;
}

function ejectToLogin() {
  auth.clearToken();
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuth, headers, ...rest } = options;

  const send = async (bearer: string | null) => {
    // Multi-site : l'officine choisie voyage à côté du jeton (voir SITE_KEY).
    const siteId = skipAuth ? null : auth.getSite();
    return fetch(`${API_URL}${path}`, {
      ...rest,
      headers: {
        // N'ajoute le Content-Type JSON que s'il y a un corps : Fastify rejette
        // une requête vide (POST/PATCH sans body) si le Content-Type est présent.
        ...(rest.body != null ? { 'Content-Type': 'application/json' } : {}),
        ...(bearer && !skipAuth ? { Authorization: `Bearer ${bearer}` } : {}),
        ...(siteId ? { 'X-Site-Id': siteId } : {}),
        ...headers,
      },
    });
  };

  // CLERK — qui est connecté COMMENT ne se déduit plus du domaine.
  //
  // Depuis que Clerk cohabite avec le mot de passe sur `pharmarh.fr`, deux
  // personnes sur le même site peuvent être connectées de deux façons
  // différentes. Le critère est donc la présence d'une session Clerk, pas
  // l'adresse du site : sinon on enverrait un jeton Clerk à quelqu'un connecté
  // par mot de passe, et réciproquement.
  //
  // Les jetons Clerk expirent vite : on en redemande un frais à chaque appel,
  // c'est Clerk qui les met en cache.
  // Un poste comptoir ignore Clerk, même si une session du hub traîne dans ce
  // navigateur : sinon l'ordinateur du comptoir repartirait avec l'identité
  // pleine du titulaire à la première requête, et tout ce mode ne servirait à
  // rien. L'installation ferme d'ailleurs la session Clerk de la machine.
  const clerkSession = hasClerkSession() && !counter.isPost();
  let token = auth.getToken();
  if (!skipAuth && clerkSession) {
    const clerkToken = await getFreshClerkToken();
    if (clerkToken) token = clerkToken;
  }

  let response = await send(token);

  // 401 : le jeton d'accès a expiré. Avant d'éjecter qui que ce soit, on essaie
  // d'en obtenir un neuf — c'est exactement ce à quoi sert le refresh token.
  // Hors sujet pour une session Clerk : elle a ses propres jetons, et
  // `/refresh` ne les connaît pas.
  // Poste comptoir : c'est la session de GESTION qui a expiré (30 min), pas le
  // poste. On la referme et on rejoue l'appel avec le jeton de la machine —
  // l'écran retombe sur les trois onglets au lieu d'éjecter tout le monde.
  if (response.status === 401 && !skipAuth && counter.isPost()) {
    if (counter.isUnlocked()) {
      counter.lock();
      response = await send(counter.token());
    }
  } else if (response.status === 401 && !skipAuth && !clerkSession) {
    refreshing ??= refreshAccessToken().finally(() => {
      refreshing = null;
    });
    const fresh = await refreshing;
    if (fresh) response = await send(fresh);
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const body: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    // Toujours 401 après la tentative de renouvellement : la session est bel et
    // bien finie.
    if (response.status === 401 && !skipAuth) {
      if (clerkSession) {
        // Session Clerk valide, mais le serveur ne reconnaît personne : aucun
        // compte Officina ne porte cette adresse. Éjecter vers /login ferait
        // une boucle (la session Clerk est toujours là, on rechargerait, on
        // reprendrait un 401). On trace, et l'écran de connexion affiche le
        // vrai problème — c'est une adresse à rapprocher, pas un mot de passe
        // à retaper.
        // eslint-disable-next-line no-console
        console.error(
          `[clerk] 401 — aucun compte Officina pour ${clerkEmail() ?? 'cette adresse'}`
        );
      } else if (counter.isPost()) {
        // POSTE COMPTOIR — un 401 ne suffit pas à démonter la machine.
        //
        // C'est ce qui cassait l'installation : l'application interrogeait
        // `/api/pharmacy/onboarding-status` (une route réservée à Clerk) avec le
        // jeton du comptoir, prenait un 401 sans rapport, et le poste se
        // désinstallait tout seul trois secondes après avoir été créé.
        //
        // Seul `/api/auth/me` fait foi : c'est LUI qui dit qui est là. S'il
        // refuse le jeton de la machine, ce jeton est mort (30 jours passés,
        // pharmacie supprimée) et il faut réinstaller le poste. Tout autre 401
        // remonte à l'appelant, comme n'importe quelle erreur.
        if (path.startsWith('/api/auth/me')) {
          counter.uninstall();
          ejectToLogin();
        }
      } else {
        ejectToLogin();
      }
    }
    // Le backend renvoie tantôt { message }, tantôt { error } (français prêt à
    // afficher). On privilégie message, puis error, sinon le statut HTTP brut.
    const b = isJson && body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    const message = b && typeof b.message === 'string'
      ? b.message
      : b && typeof b.error === 'string'
        ? b.error
        : response.statusText;
    throw new ApiError(response.status, body, message);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { method: 'GET', ...options }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined, ...options }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined, ...options }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined, ...options }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { method: 'DELETE', ...options }),
};
